<!--
Copyright (C) 2020 Reciprocity, Inc - All Rights Reserved
Unauthorized use, copying, distribution, displaying, or public performance
of this file, via any medium, is strictly prohibited. All information
contained herein is proprietary and confidential and may not be shared
with any third party without the express written consent of Reciprocity, Inc.
-->

<template>
  <generic-card
    :ok-disabled="!canEdit || isSaving"
    :ok-title="isEditing ? 'Save' : 'Edit'"
    :ok-loader="isSaving"
    :cancel-disabled="!openInJiraUrl && !backButton"
    :cancel-title="backButton ? 'Back' : 'Open in Jira'"
    :cancel-variant="backButton ? 'link' : 'outline-primary'"
    @ok="editOrSave"
    @cancel="cancel"
  >
    <template slot="modal-header">
      <!-- Leaving the slot completely empty, would render defaults -->
      <span class="d-none" />
    </template>

    <div class="jira-issue">
      <div class="row">
        <div class="col-md-8 jira-issue-main-column">
          <div class="jira-issue-summary-container">
            <issue-key v-if="!isEditing" :title="issue.key" :link="openInJiraUrl" />
            <issue-field-value :field="systemFieldsMap.summary" :issue="issue" :is-editing="isEditing" />
          </div>
          <h6 v-show="isEditing" class="jira-issue-section-title">
            Details
          </h6>
          <div class="jira-issue-details mb-4">
            <div class="row">
              <div class="col-md-6">
                <issue-field
                  v-if="!isEditing"
                  :field="systemFieldsMap.issuetype"
                  :issue="issue"
                  :is-editing="isEditing"
                  :size="8"
                />
              </div>
              <div class="col-md-6">
                <issue-field
                  v-if="!isEditing"
                  :field="systemFieldsMap.status"
                  :issue="issue"
                  :is-editing="isEditing"
                  :size="8"
                  :extra="{
                    issueTransitions: issueTransitions,
                    transitionIssue: transitionIssue,
                  }"
                />
              </div>
            </div>
            <div class="row">
              <div class="col">
                <issue-field :field="systemFieldsMap.priority" :issue="issue" :is-editing="isEditing" :size="8" />
              </div>
              <div v-if="!isEditing" class="col">
                <issue-field :field="systemFieldsMap.resolution" :issue="issue" :is-editing="isEditing" :size="8" />
              </div>
            </div>
            <div class="row">
              <div class="col">
                <issue-field :field="systemFieldsMap.labels" :issue="issue" :is-editing="isEditing" :size="8" />
                <issue-field
                  v-for="field in customFieldsDetails"
                  :key="field.key"
                  :field="field"
                  :issue="issue"
                  :is-editing="isEditing"
                  :size="8"
                />
              </div>
              <div v-if="!isEditing" class="col" />
            </div>
            <div v-if="systemFieldsMap.components" class="row">
              <div class="col">
                <issue-field
                  :field="systemFieldsMap.components"
                  :issue="issue"
                  :is-editing="isEditing"
                  :size="8"
                  :extra="peopleFieldsExtra"
                  :project-id="issue.fields.project.id"
                  :issue-type-id="issue.fields.issuetype.id"
                />
              </div>
              <div v-if="!isEditing" class="col" />
            </div>
          </div>

          <h6 class="jira-issue-section-title">Description</h6>
          <div class="jira-issue-description mb-4">
            <issue-field-value :field="systemFieldsMap.description" :issue="issue" :is-editing="isEditing" />
          </div>

          <div v-if="!isEditing && issue.fields.attachment && issue.fields.attachment.length">
            <h6 class="jira-issue-section-title mb-3">Attachments</h6>
            <div class="jira-issue-attachments mb-4">
              <issue-attachment
                v-for="attachment in issue.fields.attachment"
                :key="attachment.id"
                :attachment="attachment"
              />
            </div>
          </div>

          <div v-if="!isEditing && issue.fields.comment && issue.fields.comment.comments.length">
            <h6 class="jira-issue-section-title mb-3">Comments</h6>
            <div class="jira-issue-comments mb-4">
              <issue-comment
                v-for="(comment, idx) in issue.fields.comment.comments"
                :key="comment.id"
                :comment="comment"
                :rendered-comment="issue.renderedFields.comment.comments[idx]"
              />
            </div>
          </div>

          <issue-comment-add v-if="!isEditing && addComment" :add-comment="addComment" />
        </div>
        <div class="col-md-4 jira-issue-side-column">
          <h6 v-show="isEditing" class="jira-issue-section-title">
            People
          </h6>
          <div class="jira-issue-people">
            <issue-field
              :field="systemFieldsMap.assignee"
              :issue="issue"
              :is-editing="isEditing"
              :size="8"
              :extra="peopleFieldsExtra"
              :project-id="issue.fields.project.id"
              :issue-type-id="issue.fields.issuetype.id"
            />
            <issue-field
              :field="systemFieldsMap.reporter"
              :issue="issue"
              :is-editing="isEditing"
              :size="8"
              :extra="peopleFieldsExtra"
              :project-id="issue.fields.project.id"
              :issue-type-id="issue.fields.issuetype.id"
            />
            <issue-field
              v-for="field in customFieldsPeople"
              :key="field.key"
              :field="field"
              :issue="issue"
              :is-editing="isEditing"
              :size="8"
              :extra="peopleFieldsExtra"
              :project-id="issue.fields.project.id"
              :issue-type-id="issue.fields.issuetype.id"
            />
            <issue-field :field="systemFieldsMap.votes" :issue="issue" :is-editing="isEditing" :size="8" />
            <issue-field :field="systemFieldsMap.watches" :issue="issue" :is-editing="isEditing" :size="8" />
          </div>
          <h6 v-show="isEditing" class="jira-issue-section-title mt-4">
            Dates
          </h6>
          <div class="jira-issue-dates">
            <issue-field :field="systemFieldsMap.duedate" :issue="issue" :is-editing="isEditing" :size="8" />
            <issue-field :field="systemFieldsMap.created" :issue="issue" :is-editing="isEditing" :size="8" />
            <issue-field :field="systemFieldsMap.updated" :issue="issue" :is-editing="isEditing" :size="8" />
            <issue-field :field="systemFieldsMap.resolutiondate" :issue="issue" :is-editing="isEditing" :size="8" />
            <issue-field
              v-for="field in customFieldsDates"
              :key="field.key"
              :field="field"
              :issue="issue"
              :is-editing="isEditing"
              :size="8"
            />
          </div>
        </div>
      </div>
    </div>
  </generic-card>
</template>

<script>
import { fromPairs } from 'lodash';

import GenericCard from '@/components/cards/GenericCard.vue';
import IssueAttachment from './IssueAttachment.vue';
import IssueComment from './IssueComment.vue';
import IssueCommentAdd from './IssueCommentAdd.vue';
import IssueField from './IssueField.vue';
import IssueFieldValue from './IssueFieldValue.vue';
import IssueKey from './IssueKey.vue';
import { transformIssueFields, getAllIssueFields } from './issueFields';

export default {
  components: {
    GenericCard,
    IssueAttachment,
    IssueComment,
    IssueCommentAdd,
    IssueField,
    IssueFieldValue,
    IssueKey,
  },
  props: {
    issue: {
      type: Object,
      required: true,
    },
    issueFields: {
      type: Object,
      required: true,
    },
    issueEditFields: {
      type: Object,
      required: false,
      default: null,
    },
    issueTransitions: {
      type: Array,
      required: false,
      default: null,
    },
    isEditing: {
      type: Boolean,
      required: false,
      default: false,
    },
    isSaving: {
      type: Boolean,
      required: false,
      default: false,
    },
    canEdit: {
      type: Boolean,
      required: false,
      default: false,
    },
    openInJiraUrl: {
      type: String,
      required: false,
      default: null,
    },
    edit: {
      type: Function,
      required: false,
      default: null,
    },
    save: {
      type: Function,
      required: false,
      default: null,
    },
    transitionIssue: {
      type: Function,
      required: false,
      default: null,
    },
    addComment: {
      type: Function,
      required: false,
      default: null,
    },
    backButton: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    allIssueFields() {
      if (this.isEditing) {
        return transformIssueFields(this.issueEditFields);
      } else {
        return getAllIssueFields(this.issueFields);
      }
    },
    systemFields() {
      return this.allIssueFields.filter(field => field.schema && field.schema.system);
    },
    systemFieldsMap() {
      return fromPairs(this.systemFields.map(field => [field.key, field]));
    },
    customFields() {
      return this.allIssueFields.filter(field => field.schema && field.schema.custom);
    },
    customFieldsDetails() {
      return this.customFields.filter(
        field => field.schema.type !== 'user' && field.schema.type !== 'date' && field.schema.type !== 'datetime'
      );
    },
    customFieldsPeople() {
      return this.customFields.filter(field => field.schema.type === 'user');
    },
    customFieldsDates() {
      return this.customFields.filter(field => field.schema.type === 'date' || field.schema.type === 'datetime');
    },
    peopleFieldsExtra() {
      return {
        projectId: this.issue.fields.project.id,
        issueTypeId: this.issue.fields.issuetype.id,
      };
    },
  },
  methods: {
    cancel() {
      if (this.backButton) {
        this.$emit('close');
      } else {
        window.open(this.openInJiraUrl, '_blank');
      }
    },
    editOrSave() {
      this.isEditing ? this.save() : this.edit();
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~@/design/colors';

.jira-issue {
  &-actions {
    .btn {
      margin-left: 10px;
    }
  }

  &-side-column {
    border-left: 1px solid $color-athens-gray;
    margin: -1rem 0;
    padding-bottom: 1rem;
    padding-top: calc(86px + 1rem);
  }

  &-summary-container {
    flex-grow: 1;
  }
  &-section-title {
    font-weight: bold;
  }
}
</style>
